import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getLogoBase64 } from '../../utils/logoBase64';
import { usesUnifiedDates, getDatesForPDF, getDateLabelForPDF } from '../../utils/unifiedDateStorage';

// Company branding (same as invoice)
const BRAND_COLOR = '#a20000';
const BRAND_COLOR_RGB = [162, 0, 0];

// Company information (same as invoice)
const COMPANY_INFO = {
  name: 'Beema Scaffolding (Singapore) Pte Ltd',
  address: '102F Pasir Panjang Road, #04-12',
  address2: 'Citilink Warehouse Complex',
  address3: 'Singapore 118530',
  phone: '+65 85123494',
  website: 'beemascaffolding.sg',
  email: 'contact@beemascaffolding.sg',
  uen: '202340913H'
};

const CONTACT_INFO = {
  name: 'Lina',
  phone: '+65 85123494',
  email: 'contact@beemascaffolding.sg'
};

/**
 * Generate and download delivery order PDF
 * @param {Object} invoiceData - Invoice data
 * @param {Object} materialList - Material list data (optional)
 */
export const generateDeliveryOrderPDF = async (invoiceData, materialList = null) => {
  try {
    console.log('Starting delivery order PDF generation...');
    
    // Validate required data
    if (!invoiceData) {
      throw new Error('Invoice data is required');
    }

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 12;
    const footerHeight = 15;
    const safeAreaBottom = pageHeight - footerHeight;
    let currentY = margin;

    // Page management functions
    const checkPageBreak = (requiredSpace = 10) => {
      if (currentY + requiredSpace > safeAreaBottom) {
        addFooter();
        pdf.addPage();
        currentY = margin;
        return true;
      }
      return false;
    };

    const addFooter = (pageNum = null, totalPagesCount = null) => {
      const footerY = pageHeight - 10;
      const currentPage = pageNum || pdf.internal.getCurrentPageInfo().pageNumber;
      const totalPages = totalPagesCount || pdf.internal.getNumberOfPages();
      
      // Footer separator line
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.5);
      pdf.line(margin, footerY - 5, pageWidth - margin, footerY - 5);
      
      // Footer content
      pdf.setTextColor(80, 80, 80);
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.text('If you have any questions about this delivery order, please contact:', margin, footerY);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${CONTACT_INFO.name}, ${CONTACT_INFO.phone}, ${CONTACT_INFO.email}`, margin, footerY + 6);
      
      // Page numbering (right aligned)
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Page ${currentPage} of ${totalPages}`, pageWidth - margin, footerY + 6, { align: 'right' });
    };

    // Function to add footers to all pages after content is complete
    const addFootersToAllPages = () => {
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        addFooter(i, totalPages);
      }
    };

    // Helper function to add text with better positioning
    const addText = (text, x, y, options = {}) => {
      const {
        fontSize = 10,
        fontStyle = 'normal',
        align = 'left',
        maxWidth = pageWidth - 2 * margin,
        color = [0, 0, 0]
      } = options;

      pdf.setFontSize(fontSize);
      pdf.setFont('helvetica', fontStyle);
      pdf.setTextColor(color[0], color[1], color[2]);

      if (align === 'center') {
        pdf.text(text || '', pageWidth / 2, y, { align: 'center' });
      } else if (align === 'right') {
        pdf.text(text || '', x, y, { align: 'right' });
      } else {
        pdf.text(text || '', x, y);
      }
      
      return y + (fontSize * 0.35) + 2;
    };

    // Load and add logo (same as invoice)
    let logoLoaded = false;
    
    try {
      console.log('Loading logo for delivery order...');
      
      const logoBase64 = await getLogoBase64();
      
      const tempImg = new Image();
      tempImg.src = logoBase64;
      await new Promise((resolve, reject) => {
        tempImg.onload = resolve;
        tempImg.onerror = reject;
        setTimeout(reject, 3000);
      });
      
      // Calculate dimensions to preserve aspect ratio
      const maxWidth = 30;
      const maxHeight = 20;
      const aspectRatio = tempImg.width / tempImg.height;
      
      let logoWidth, logoHeight;
      if (aspectRatio > maxWidth / maxHeight) {
        logoWidth = maxWidth;
        logoHeight = maxWidth / aspectRatio;
      } else {
        logoHeight = maxHeight;
        logoWidth = maxHeight * aspectRatio;
      }
      
      pdf.addImage(logoBase64, 'JPEG', margin, currentY, logoWidth, logoHeight);
      console.log('Logo loaded successfully for delivery order');
      logoLoaded = true;
    } catch (error) {
      console.error('Could not load logo for delivery order:', error);
    }

    // HEADER SECTION - Company name next to logo, DELIVERY ORDER on right
    pdf.setTextColor(BRAND_COLOR_RGB[0], BRAND_COLOR_RGB[1], BRAND_COLOR_RGB[2]);
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text(COMPANY_INFO.name, margin + 32, currentY + 8);
    pdf.setFontSize(11);
    pdf.text('DELIVERY ORDER', pageWidth - margin, currentY + 18, { align: 'right' });
    
    currentY += 25;

    // Company address block (same as invoice)
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    currentY = addText(COMPANY_INFO.address, margin, currentY, { fontSize: 8 });
    currentY = addText(COMPANY_INFO.address2, margin, currentY, { fontSize: 8 });
    currentY = addText(COMPANY_INFO.address3, margin, currentY, { fontSize: 8 });
    currentY += 1;
    currentY = addText(`Phone: ${COMPANY_INFO.phone}`, margin, currentY, { fontSize: 8 });
    currentY = addText(`Website: ${COMPANY_INFO.website}`, margin, currentY, { fontSize: 8 });
    currentY = addText(`Email: ${COMPANY_INFO.email}`, margin, currentY, { fontSize: 8 });
    currentY = addText(`UEN: ${COMPANY_INFO.uen}`, margin, currentY, { fontSize: 8 });

    // Date and delivery order number (right aligned)
    const dateInfoY = margin + 25;
    addText(`DATE: ${invoiceData.date || new Date().toLocaleDateString('en-GB')}`, pageWidth - margin, dateInfoY, { 
      fontSize: 10, 
      align: 'right' 
    });
    
    // Generate delivery order number based on invoice number
    const deliveryOrderNumber = invoiceData.invoiceNumber ? `DO-${invoiceData.invoiceNumber}` : 'DO-DRAFT';
    addText(`DELIVERY ORDER #: ${deliveryOrderNumber}`, pageWidth - margin, dateInfoY + 7, { 
      fontSize: 10, 
      align: 'right' 
    });
    
    // Reference to original invoice
    addText(`REF INVOICE: ${invoiceData.invoiceNumber || 'DRAFT'}`, pageWidth - margin, dateInfoY + 14, { 
      fontSize: 10, 
      align: 'right' 
    });

    // Add horizontal line separator
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.5);
    pdf.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 8;

    // TWO-COLUMN LAYOUT FOR CUSTOMER AND JOB DETAILS (same as invoice)
    const leftColX = margin;
    const rightColX = pageWidth / 2 + 10;
    const sectionStartY = currentY;

    // LEFT COLUMN - Customer Details
    let leftY = sectionStartY + 2;
    
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(0, 0, 0);

    // Company/Customer name
    const customerName = invoiceData.companyName || invoiceData.customerName || 'N/A';
    pdf.setFont('helvetica', 'bold');
    pdf.text('To:', leftColX, leftY);
    const toLabelWidth = pdf.getTextWidth('To: ');
    pdf.setFont('helvetica', 'normal');
    pdf.text(customerName, leftColX + toLabelWidth, leftY);
    leftY += 6;
    
    // Attention
    const attnText = invoiceData.companyName ? (invoiceData.attn || 'N/A') : 'N/A';
    pdf.setFont('helvetica', 'bold');
    pdf.text('Attn:', leftColX, leftY);
    const attnLabelWidth = pdf.getTextWidth('Attn: ');
    pdf.setFont('helvetica', 'normal');
    pdf.text(attnText, leftColX + attnLabelWidth, leftY);
    leftY += 6;
    
    // Phone
    pdf.setFont('helvetica', 'bold');
    pdf.text('Phone:', leftColX, leftY);
    const phoneLabel = 'Phone: ';
    const phoneLabelWidth = pdf.getTextWidth(phoneLabel);
    pdf.setFont('helvetica', 'normal');
    const phoneValue = invoiceData.tel || 'N/A';
    let cleanPhoneValue = phoneValue.toString()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      .replace(/\s+/g, ' ')
      .replace(/[^\x20-\x7E]/g, '')
      .trim();
    
    try {
      pdf.text(cleanPhoneValue, leftColX + phoneLabelWidth, leftY);
    } catch (error) {
      console.warn('Phone rendering fallback used');
      let currentX = leftColX + phoneLabelWidth;
      for (let i = 0; i < cleanPhoneValue.length; i++) {
        const char = cleanPhoneValue[i];
        pdf.text(char, currentX, leftY);
        currentX += pdf.getTextWidth(char);
      }
    }
    leftY += 6;
    
    // Email
    pdf.setFont('helvetica', 'bold');
    pdf.text('Email:', leftColX, leftY);
    const emailLabelWidth = pdf.getTextWidth('Email: ');
    pdf.setFont('helvetica', 'normal');
    pdf.text(invoiceData.email || 'N/A', leftColX + emailLabelWidth, leftY);
    leftY += 6;

    // RIGHT COLUMN - Delivery Details
    let rightY = sectionStartY + 2;
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    
    // Invoice Type
    pdf.setFont('helvetica', 'bold');
    pdf.text('Invoice Type:', rightColX, rightY);
    const invoiceTypeLabelWidth = pdf.getTextWidth('Invoice Type: ');
    pdf.setFont('helvetica', 'normal');
    pdf.text(getInvoiceTypeLabel(invoiceData.invoiceType), rightColX + invoiceTypeLabelWidth, rightY);
    rightY += 6;
    
    // Site Address (if available)
    if (invoiceData.siteAddress && invoiceData.siteAddress.trim() !== '') {
      pdf.setFont('helvetica', 'bold');
      pdf.text('Site Address:', rightColX, rightY);
      const siteAddressLabelWidth = pdf.getTextWidth('Site Address: ');
      pdf.setFont('helvetica', 'normal');
      
      const siteAddressText = invoiceData.siteAddress;
      const availableWidth = (pageWidth / 2) - 20 - siteAddressLabelWidth;
      const siteAddressLines = pdf.splitTextToSize(siteAddressText, availableWidth);
      
      if (siteAddressLines.length > 0) {
        pdf.text(siteAddressLines[0], rightColX + siteAddressLabelWidth, rightY);
        rightY += 6;
        
        for (let i = 1; i < siteAddressLines.length; i++) {
          pdf.text(siteAddressLines[i], rightColX + siteAddressLabelWidth, rightY);
          rightY += 6;
        }
      }
    }
    
    // Display date information using unified date system
    if (usesUnifiedDates(invoiceData)) {
      const datesForPDF = getDatesForPDF(invoiceData);
      if (datesForPDF) {
        const dateLabel = getDateLabelForPDF(invoiceData);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${dateLabel}:`, rightColX, rightY);
        const datesLabelWidth = pdf.getTextWidth(`${dateLabel}: `);
        pdf.setFont('helvetica', 'normal');

        const availableWidth = (pageWidth / 2) - 20 - datesLabelWidth;
        const datesLines = pdf.splitTextToSize(datesForPDF, availableWidth);

        if (datesLines.length > 0) {
          pdf.text(datesLines[0], rightColX + datesLabelWidth, rightY);
          rightY += 6;

          for (let i = 1; i < datesLines.length; i++) {
            pdf.text(datesLines[i], rightColX + datesLabelWidth, rightY);
            rightY += 6;
          }
        }
      }
    }

    currentY = Math.max(leftY, rightY) + 3;

    // ITEMS TABLE - Simplified for delivery tracking
    const itemsData = prepareItemsForDeliveryOrder(invoiceData, materialList);
    
    if (itemsData && itemsData.length > 0) {
      // Configure autoTable for delivery order
      autoTable(pdf, {
        startY: currentY,
        head: [['#', 'Item Description', 'Quantity', 'Comments Upon Return']],
        body: itemsData,
        theme: 'grid',
        headStyles: {
          fillColor: BRAND_COLOR_RGB,
          textColor: [255, 255, 255],
          fontSize: 10,
          fontStyle: 'bold',
          halign: 'left'
        },
        bodyStyles: {
          fontSize: 9,
          textColor: [0, 0, 0],
          halign: 'left',
          minCellHeight: 8 // Reduced height for table rows
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        columnStyles: {
          0: { cellWidth: 15, halign: 'center' }, // #
          1: { cellWidth: 80, halign: 'left' },   // Item Description
          2: { cellWidth: 25, halign: 'center' }, // Quantity
          3: { cellWidth: 66, halign: 'left' } // Comments (larger space)
        },
        margin: { left: margin, right: margin },
        tableWidth: 186,
        styles: {
          cellPadding: 3,
          lineColor: [0, 0, 0],
          lineWidth: 0.1
        },
        pageBreak: 'auto',
        margin: { 
          left: margin, 
          right: margin, 
          top: margin, 
          bottom: footerHeight + 5 
        }
      });

      currentY = pdf.lastAutoTable.finalY + 15;
    }

    // SIGNATURE SECTIONS
    checkPageBreak(60); // Ensure enough space for signature sections

    // COLLECTION SECTION
    pdf.setTextColor(BRAND_COLOR_RGB[0], BRAND_COLOR_RGB[1], BRAND_COLOR_RGB[2]);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('COLLECTION ACKNOWLEDGMENT', margin, currentY);
    currentY += 10;

    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');

    // Collection fields
    const fieldSpacing = 8;
    const signatureBoxHeight = 15;
    
    // Date and Time fields (side by side)
    pdf.text('Date:', margin, currentY);
    pdf.line(margin + 15, currentY - 1, margin + 70, currentY - 1); // Date line
    
    pdf.text('Time:', margin + 80, currentY);
    pdf.line(margin + 95, currentY - 1, margin + 150, currentY - 1); // Time line
    currentY += fieldSpacing;

    // Receiver Name
    pdf.text('Receiver Name:', margin, currentY);
    pdf.line(margin + 35, currentY - 1, pageWidth - margin, currentY - 1);
    currentY += fieldSpacing;

    // Receiver Signature
    pdf.text('Receiver Signature:', margin, currentY);
    pdf.rect(margin + 40, currentY - 5, 80, signatureBoxHeight); // Signature box
    currentY += signatureBoxHeight + 3;

    // Contact Number
    pdf.text('Contact Number:', margin, currentY);
    pdf.line(margin + 35, currentY - 1, pageWidth - margin, currentY - 1);
    currentY += 15;

    // RETURN SECTION
    checkPageBreak(70); // Ensure enough space for return section

    pdf.setTextColor(BRAND_COLOR_RGB[0], BRAND_COLOR_RGB[1], BRAND_COLOR_RGB[2]);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('RETURN ACKNOWLEDGMENT', margin, currentY);
    currentY += 10;

    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');

    // Return fields (same structure as collection)
    // Date and Time fields
    pdf.text('Date:', margin, currentY);
    pdf.line(margin + 15, currentY - 1, margin + 70, currentY - 1);
    
    pdf.text('Time:', margin + 80, currentY);
    pdf.line(margin + 95, currentY - 1, margin + 150, currentY - 1);
    currentY += fieldSpacing;

    // Receiver Name
    pdf.text('Receiver Name:', margin, currentY);
    pdf.line(margin + 35, currentY - 1, pageWidth - margin, currentY - 1);
    currentY += fieldSpacing;

    // Receiver Signature
    pdf.text('Receiver Signature:', margin, currentY);
    pdf.rect(margin + 40, currentY - 5, 80, signatureBoxHeight);
    currentY += signatureBoxHeight + 3;

    // Contact Number
    pdf.text('Contact Number:', margin, currentY);
    pdf.line(margin + 35, currentY - 1, pageWidth - margin, currentY - 1);
    currentY += fieldSpacing;

    // Items Returned in Good Condition
    pdf.text('Items Returned in Good Condition:', margin, currentY);
    
    // Yes checkbox
    pdf.rect(margin + 75, currentY - 4, 4, 4);
    pdf.text('Yes', margin + 82, currentY);
    
    // No checkbox
    pdf.rect(margin + 100, currentY - 4, 4, 4);
    pdf.text('No', margin + 107, currentY);
    currentY += 10;

    // Comments section
    pdf.text('Comments:', margin, currentY);
    currentY += 5;
    
    // Multiple comment lines
    for (let i = 0; i < 3; i++) {
      pdf.line(margin, currentY, pageWidth - margin, currentY);
      currentY += 6;
    }

    // Add footers to all pages
    addFootersToAllPages();

    // Generate filename
    const companyName = (invoiceData.companyName || invoiceData.customerName || 'Customer').replace(/[^a-zA-Z0-9]/g, '_');
    const invoiceNumber = invoiceData.invoiceNumber || 'DRAFT';
    const fileName = `DO-${invoiceNumber}.pdf`;
    
    console.log('Delivery order PDF generated successfully');
    
    // Save the PDF
    pdf.save(fileName);
    
    return true;
    
  } catch (error) {
    console.error('Delivery order PDF generation error:', error);
    throw new Error(`Delivery order PDF generation failed: ${error.message}`);
  }
};

/**
 * Prepare items data for delivery order table
 * @param {Object} invoiceData - Invoice data
 * @param {Object} materialList - Material list data (optional)
 * @returns {Array} Table data for delivery order
 */
const prepareItemsForDeliveryOrder = (invoiceData, materialList) => {
  const items = [];
  
  console.log('📦 Preparing items for delivery order...');
  console.log('   - Invoice ID:', invoiceData.id);
  console.log('   - Material list provided:', !!materialList);
  console.log('   - Material list has requirements:', !!(materialList?.requirements));
  
  // Priority 1: Use material list if available (shows actual materials)
  if (materialList && materialList.requirements && Object.keys(materialList.requirements).length > 0) {
    console.log('✅ Using material list requirements (Priority 1)');
    let index = 1;
    
    Object.entries(materialList.requirements).forEach(([materialType, requirement]) => {
      console.log(`   - Material ${index}: ${materialType} x ${requirement.totalQuantity}`);
      items.push([
        index.toString(),
        materialType, // This shows actual material names like "Aluminum Scaffold 4ft x 4ft"
        requirement.totalQuantity.toString(),
        '' // Empty comments field for manual entry
      ]);
      index++;
    });
    
    console.log(`📋 Added ${items.length} material items from material list`);
  }
  // Priority 2: Fall back to invoice line items (shows descriptions)
  else if (invoiceData.lineItems && Array.isArray(invoiceData.lineItems)) {
    console.log('⚠️ Using invoice line items (Priority 2 fallback)');
    console.log('   Reason: Material list not available or has no requirements');
    
    const validLineItems = invoiceData.lineItems.filter(item => 
      item && item.description && item.description.trim() !== ''
    );
    
    validLineItems.forEach((item, index) => {
      console.log(`   - Line item ${index + 1}: ${item.description} x ${item.quantity || 0}`);
      items.push([
        (index + 1).toString(),
        item.description || '', // Generic descriptions like "10 days rental"
        (item.quantity || 0).toString(),
        '' // Empty comments field for manual entry
      ]);
    });
    
    console.log(`📝 Added ${items.length} items from invoice line items`);
  } else {
    console.error('❌ No items available - neither material list nor line items found!');
  }
  
  console.log(`📊 Total items prepared for delivery order: ${items.length}`);
  return items;
};

/**
 * Get display label for invoice type
 * @param {string} type - Invoice type
 * @returns {string} Display label
 */
const getInvoiceTypeLabel = (type) => {
  const labels = {
    'purchase': 'Purchase',
    'rental': 'Rental',
    'rental_extension': 'Rental Extension',
    'scaffolding_service': 'Scaffolding Service',
    'damaged_items': 'Damaged Items'
  };
  return labels[type] || type;
};

export default generateDeliveryOrderPDF;
